<script setup lang="ts">
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { __ } from '@/js/stores/expressions';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { ref, watch } from 'vue';

    const props = defineProps<{
        filterStoreName: FilterStoreName;
    }>();

    const filter = useFilterStoreByName(props.filterStoreName);

    const recordingStartMinute = ref<number | null>(null);
    const recordingStartSecond = ref<number | null>(null);
    const recordingEndMinute = ref<number | null>(null);
    const recordingEndSecond = ref<number | null>(null);

    watch(
        [() => filter.all?.recordingStart, () => filter.all?.recordingEnd],
        () => {
            if (filter.all && filter.all.recordingStart !== undefined) {
                recordingStartMinute.value = Math.floor(filter.all.recordingStart / 60) || null;
                recordingStartSecond.value = filter.all.recordingStart % 60 || null;
            }

            if (filter.all && filter.all.recordingEnd !== undefined) {
                recordingEndMinute.value = Math.floor(filter.all.recordingEnd / 60) || null;
                recordingEndSecond.value = filter.all.recordingEnd % 60 || null;
            }
        },
        { immediate: true, deep: true }
    );

    watch(
        [recordingStartMinute, recordingStartSecond],
        () => {
            const start =
                Number((recordingStartMinute.value || 0) * 60) +
                Number(recordingStartSecond.value || 0);

            if (filter.all) {
                filter.all.recordingStart = start;
            }

            let recordingStartLabel = `${__('start-time')} (${recordingStartMinute.value || 0} ${__('minutes-short')}`;
            if ((recordingStartSecond.value || 0) > 0) {
                recordingStartLabel += `, ${recordingStartSecond.value || 0} ${__('seconds-short')}`;
            }
            recordingStartLabel += ')';

            filter.recordingStartLabel = recordingStartLabel;
        },
        { immediate: true }
    );

    watch(
        [recordingEndMinute, recordingEndSecond],
        () => {
            const end =
                Number((recordingEndMinute.value || 0) * 60) +
                Number(recordingEndSecond.value || 0);

            if (filter.all) {
                filter.all.recordingEnd = end;
            }

            let recordingEndLabel = `${__('end-time')} (`;

            if (!recordingEndMinute.value && !recordingEndSecond.value) {
                recordingEndLabel += `${__('no-limit')}`;
            } else {
                if (recordingEndMinute.value || recordingEndSecond.value) {
                    recordingEndLabel += `${recordingEndMinute.value || 0} ${__('minutes-short')}`;
                }
                if ((recordingEndSecond.value || 0) > 0) {
                    recordingEndLabel += `, ${recordingEndSecond.value || 0} ${__('seconds-short')}`;
                }
            }
            recordingEndLabel += ')';

            filter.recordingEndLabel = recordingEndLabel;
        },
        { immediate: true }
    );
</script>

<template>
    <div class="flex flex-wrap gap-2 px-4 mb-6 mt-4">
        <label class="w-full block font-semibold">{{ filter.recordingStartLabel }}</label>
        <div class="relative">
            <input
                v-model="recordingStartMinute"
                type="tel"
                class="Time-input custom-focus"
                placeholder="0"
                min="0"
                :aria-label="__('start-minute')"
            />
            <span class="Time-input__label">{{ __('minutes-short') }}</span>
        </div>
        <div class="relative">
            <input
                v-model="recordingStartSecond"
                type="tel"
                class="Time-input custom-focus"
                placeholder="0"
                min="0"
                max="59"
                :aria-label="__('start-second')"
            />
            <span class="Time-input__label">{{ __('seconds-short') }}</span>
        </div>
    </div>
    <div class="flex flex-wrap gap-2 px-4 mb-6">
        <label class="w-full block font-semibold">{{ filter.recordingEndLabel }}</label>
        <div class="relative">
            <input
                v-model="recordingEndMinute"
                type="tel"
                class="Time-input custom-focus"
                :placeholder="recordingEndMinute || recordingEndSecond ? '0' : '-'"
                min="0"
                :aria-label="__('end-minute')"
            />
            <span class="Time-input__label">{{ __('minutes-short') }}</span>
        </div>
        <div class="relative">
            <input
                v-model="recordingEndSecond"
                type="tel"
                class="Time-input custom-focus"
                :placeholder="recordingEndMinute || recordingEndSecond ? '0' : '-'"
                min="0"
                max="59"
                :aria-label="__('end-second')"
            />
            <span class="Time-input__label">{{ __('seconds-short') }}</span>
        </div>
    </div>
</template>
